import { Record as record, List } from 'immutable';

import { resolve } from '../helpers/LinkMapper';
import Customer from './customer/Customer';
import config from '../config';

const defaults = {
    customer: null
};

class User extends record(defaults) {

    static fromJSON (json = {}) {
        const customerData = json.customer ? json.customer : Object.values(json).length ? json : undefined;
        const customer = resolve(customerData).as(Customer);
        return new User({ customer });
    }

    getCustomerProp (...path) {
        return this.getIn([ 'customer', ...path ]);
    }

    get accessToken () {
        return this.getCustomerProp('accessToken', 'accessToken');
    }

    get accessTokenDevice () {
        return this.getCustomerProp('accessToken', 'customerDevice');
    }

    get refreshToken () {
        return this.getCustomerProp('accessToken', 'refreshToken');
    }

    setAuthToken (token) {
        if (this.customer) {
            return this.setIn([ 'customer', 'accessToken'], token);
        } else {
            return User.fromJSON({ customer: { accessToken: token }});
        }
    }

    get authToken () {
        return this.getCustomerProp('accessToken');
    }

    isLoggedIn () {
        return !!(this.authToken && this.authToken.isValid());
    }

    isFullyLoaded () {
        return this.isLoggedIn() && this.created;
    }

    isChild () {
        return this.customer && this.customer.isChild();
    }

    isAdult () {
        return !this.isChild();
    }

    hasIdentityProvider (provider) {
        return this.customer && this.customer.hasIdentityProvider(provider);
    }

    removeIdentityProvider (provider) {
        if (this.customer) {
            const newCustomer = this.customer && this.customer.removeIdentityProvider(provider);

            return this.set('customer', newCustomer);
        }

        return false;
    }

    get fullName () {
        return [this.title, this.firstNames, this.surname].join(' ');
    }

    get title () {
        return this.getCustomerProp('title');
    }

    get firstNames () {
        return this.getCustomerProp('firstNames');
    }

    get surname () {
        return this.getCustomerProp('surname');
    }

    get telephoneNumber () {
        return this.getCustomerProp('telephoneNumber');
    }

    setTelephoneNumber (telephoneNumber) {
        return this.setIn(['customer', 'telephoneNumber'], telephoneNumber);
    }

    get email () {
        return this.getCustomerProp('email');
    }

    get id () {
        // id comes from the access token when logging in, otherwise it's in the customer :-/
        const accessToken = this.getCustomerProp('accessToken');
        return this.getCustomerProp('id') || (accessToken && accessToken.getIn(['customer', 'id']));
    }

    get smartcardRegistered () {
        return this.getCustomerProp('smartcardRegistered');
    }

    setHasRegisteredSmartcard (value) {
        return this.setIn(['customer', 'smartcardRegistered'], value);
    }

    setOneClickDelayRepayOptedIn (value) {
        return this.setIn(['customer', 'autoDrOptedIn'], value);
    }

    get addresses () {
        return this.getCustomerProp('addresses') || new List();
    }

    get deliveryAddresses () {
        const addresses = this.addresses;
        return addresses.filter((address) => address.contactName && address.country === config.get('defaultDeliveryCountry'));
    }

    setAddresses (addresses) {
        return this.setIn(['customer', 'addresses'], addresses);
    }

    get created () {
        return this.getCustomerProp('created');
    }

    get dateOfBirth () {
        return this.getCustomerProp('dateOfBirth');
    }

    get preferences () {
        return this.getCustomerProp('preferences');
    }

    setIdentityProviders (identityProviders) {
        return this.setIn(['customer', 'identityProviders'], identityProviders);
    }

    setPreferences (preferences) {
        return this.setIn(['customer', 'preferences' ], preferences);
    }

    get autoDrOptedIn () {
        return this.getIn(['customer', 'autoDrOptedIn']);
    }

    setAutoDrOptedIn (isSubscribed = false) {
        return this.setIn(['customer', 'autoDrOptedIn'], isSubscribed);
    }

    get isOver16 () {
        return this.getCustomerProp('isOver16');
    }

    setIsOver16 (isOver16) {
        return this.setIn(['customer', 'isOver16'], isOver16);
    }

    get brandChosen () {
        return this.getCustomerProp('brandChosen');
    }

    get deactivationRequests () {
        return this.getCustomerProp('deactivationRequests');
    }

    getSmartcardAddress () {
        return this.addresses.find((address) => address.isSmartcardAddress());
    }
}

export default User;
